import React from "react";
import { Grid, TextField, Typography, FormHelperText as RawErrorText } from "@material-ui/core";
import styled from "styled-components";
import { RawLinkButton } from "../../LinkButton";
import FormHelperText from "../../forms/FormHelperText";
import { GetMarkdown, getSelectedOptions, getTotalPrice, resetOptions } from "../../../functions/formFunctions";
import { IOption } from "../../../models/IOption";
import { DatabaseTypes, OneRosterDurationOption, SandboxDurationPricing } from "./SandboxOptions";
import ISupportTicketCategory from "../../../models/ISupportTicketCategory";
import ReadOnlyOption from "./ReadOnlyOption";
import CheckboxOptionsStyled from "./CheckboxOptionsStyled";
import Theme from "../../../Theme";
import ReadOnlyRadioOption from "../../forms/ReadOnlyRadioOption";

const FullWidthTextField = styled(TextField)`
  min-width: 475px;
  max-width: 600px;
`;

const FormErrorText = styled(RawErrorText)`
  font-size: 14px;
  color: ${Theme.colors.error};
`;

export interface IOneRosterSandboxRequestFormProps {
  category: ISupportTicketCategory;
  onFormSubmit: (summary: string, description: string) => Promise<boolean>;
}

const OneRosterSandboxRequestForm = (props: IOneRosterSandboxRequestFormProps) => {
  const { onFormSubmit, category } = props;

  const [description, setDescription] = React.useState<string>("");
  const [dbTypes, setDbTypes] = React.useState<IOption[]>(DatabaseTypes);
  const [dbError, setDbError] = React.useState<string>("");
  const [detailError, setDetailError] = React.useState<string>("");

  const allOpts = [...dbTypes, OneRosterDurationOption];

  const reset = () => {
    setDescription("");
    setDbTypes(resetOptions(dbTypes));
  };

  React.useEffect(reset, [category]);

  const getFormErrors = () => {
    let hasErrors = false;

    if (!description || description === "") {
      setDetailError("Describe your need for a sandbox is a required field.");
      hasErrors = true;
    }

    if (!DatabaseTypes[0].Selected && !DatabaseTypes[1].Selected) {
      setDbError("A database type must be selected.");
      hasErrors = true;
    }

    return hasErrors;
  };

  const quoteTotal = (): number => getTotalPrice(allOpts);
  const hasDbError = (): boolean => (dbError && dbError !== "" ? true : false);
  const hasDetailError = (): boolean => (detailError && detailError !== "" ? true : false);

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!getFormErrors()) {
      const md = GetMarkdown(category.Name, description, quoteTotal(), getSelectedOptions(allOpts));

      if (await onFormSubmit("OneRoster Sandbox Request Form", md)) {
        reset();
      }
    }
  };

  return (
    <>
      <Grid item container spacing={3}>
        <Grid item>
          <FullWidthTextField
            id="description"
            label={"Describe your need for a sandbox"}
            variant="outlined"
            multiline={true}
            rows={4}
            rowsMax={100}
            required
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              setDescription(evt.target.value);
              setDetailError("");
            }}
            value={description}
            error={hasDetailError()}
          />
          {hasDetailError() && <FormErrorText>{detailError}</FormErrorText>}
          <FormHelperText>Include details regarding how you will utilize the sandbox</FormHelperText>
        </Grid>
        <Grid item>
          <ReadOnlyRadioOption option={OneRosterDurationOption} label="Duration" />
          <CheckboxOptionsStyled
            options={DatabaseTypes}
            onChange={opts => {
              setDbTypes(opts);
              setDbError("");
            }}
          >
            Please check database selections:
          </CheckboxOptionsStyled>
          {hasDbError() && <FormErrorText>{dbError}</FormErrorText>}
        </Grid>
      </Grid>
      <Typography variant="h5">Total Quote ${quoteTotal()}</Typography>
      <Grid item>
        <RawLinkButton first onClick={handleSubmit}>
          <>Submit Request</>
        </RawLinkButton>
      </Grid>
    </>
  );
};

export default OneRosterSandboxRequestForm;
